/**
 * Wave 3.16 — Jewellery list page.
 * Wave 3.24 — Page rewrite:
 *   - 4 KPI tiles (Pieces · Gross Weight · Net Weight · Total Value at Cost)
 *   - Cards expanded with stone weights breakdown (D / CS / P) and
 *     value-at-cost (manufacturing cost, not just the cash bill).
 *   - Goldsmith name pinned to the bottom of each card.
 *   - Search now matches gross weight (numeric) as well.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import AppShell from '@/components/AppShell';
import CompanyGate from '@/components/CompanyGate';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { SearchableSelect } from '@/components/ui/searchable-select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Diamond, Search, Image as ImageIcon, ArrowRight, ScrollText, Hammer, Gem, Sparkles, Circle, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, CalendarDays, Coins, Tag, Printer, X, Pencil, Loader2, ListChecks, CheckCheck, Check,
} from 'lucide-react';
import { formatINR, formatGrams, formatCarats, formatDate } from '@/lib/format';
import { toast } from 'sonner';
import { extractError } from '@/lib/errors';
import { JewelleryTagDialog } from '@/components/jewellery/JewelleryTagDialog';
import { JewelleryBatchPrintDialog } from '@/components/jewellery/JewelleryBatchPrintDialog';
import { fileToResizedDataUrl } from '@/lib/imageResize';

export default function Jewellery() {
  return (
    <AppShell
      title="Jewellery"
      subtitle="Finished pieces received from goldsmiths"
    >
      <CompanyGate>
        <JewelleryList />
      </CompanyGate>
    </AppShell>
  );
}

function JewelleryList() {
  // Wave 3.41 — Restricted users hide cost/value KPIs and value tiles.
  const { effectiveCompanyId, canViewValues } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [goldsmithFilter, setGoldsmithFilter] = useState('all');
  // Wave 3.25 hot-fix — date range filter on received_at; "From"/"To" are
  // ISO yyyy-mm-dd strings consistent with the rest of the app.
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  // Print Image List dialog state
  const [printDialog, setPrintDialog] = useState({
    open: false, includeBranding: true, showDetails: true, busy: false,
  });
  // Batch Tag Printing (select multiple pieces, then print their tags).
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [batchOpen, setBatchOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!effectiveCompanyId) return;
      setLoading(true);
      try {
        const res = await api.get('/jewellery', {
          params: { company_id: effectiveCompanyId },
        });
        if (!cancelled) setItems(res.data || []);
      } catch (e) {
        toast.error(extractError(e, 'Failed to load jewellery list'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [effectiveCompanyId]);

  // Build distinct goldsmith options from the loaded items.
  const goldsmithOptions = useMemo(() => {
    const m = new Map();
    for (const j of items) {
      if (j.goldsmith_id && j.goldsmith_name) {
        m.set(j.goldsmith_id, j.goldsmith_name);
      }
    }
    return [...m.entries()].map(([id, name]) => ({ id, name }));
  }, [items]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    // Wave 3.25 hot-fix — Treat dateFrom as inclusive 00:00 IST and dateTo
    // as inclusive 23:59 IST. Empty string disables that bound.
    const fromMs = dateFrom ? new Date(`${dateFrom}T00:00:00+05:30`).getTime() : null;
    const toMs = dateTo ? new Date(`${dateTo}T23:59:59+05:30`).getTime() : null;
    return items.filter((j) => {
      if (goldsmithFilter !== 'all' && j.goldsmith_id !== goldsmithFilter) return false;
      if (fromMs != null || toMs != null) {
        const ts = j.received_at ? new Date(j.received_at).getTime() : null;
        if (ts == null) return false;
        if (fromMs != null && ts < fromMs) return false;
        if (toMs != null && ts > toMs) return false;
      }
      if (!q) return true;
      // Wave 3.24 — search now also matches gross weight (numeric).
      // Wave 3.25 — search also matches Type of Jewellery + Gold Purity.
      const grossStr = (j.gross_weight_grams || 0).toFixed(3);
      return (
        (j.jewellery_no || '').toLowerCase().includes(q)
        || (j.title || '').toLowerCase().includes(q)
        || (j.source_order_no || '').toLowerCase().includes(q)
        || (j.goldsmith_name || '').toLowerCase().includes(q)
        || (j.jewellery_type || '').toLowerCase().includes(q)
        || (j.gold_purity_name || '').toLowerCase().includes(q)
        || grossStr.includes(q)
      );
    });
  }, [items, search, goldsmithFilter, dateFrom, dateTo]);

  const hasActiveFilter = !!(search || goldsmithFilter !== 'all' || dateFrom || dateTo);
  const clearFilters = () => {
    setSearch(''); setGoldsmithFilter('all'); setDateFrom(''); setDateTo('');
  };

  // ── Pagination — 30 cards per page (client-side). KPIs, Print Image List
  // and batch "Select all" all operate on the FULL `filtered` set, so only
  // the rendered grid is paged (keeps those cross-page features intact while
  // keeping the DOM light). Page resets to 1 whenever the filter changes.
  const PAGE_SIZE = 30;
  const [page, setPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  useEffect(() => { setPage(1); }, [search, goldsmithFilter, dateFrom, dateTo]);
  useEffect(() => { if (page > totalPages) setPage(totalPages); }, [page, totalPages]);
  const pageItems = useMemo(
    () => filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [filtered, page],
  );

  // ── Batch selection helpers ─────────────────────────────────────────
  const BATCH_LIMIT = 30;
  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= BATCH_LIMIT) {
        toast.error(`You can select up to ${BATCH_LIMIT} pieces for batch printing.`);
        return prev;
      }
      return [...prev, id];
    });
  };
  const selectAllFiltered = () => {
    const all = filtered.map((j) => j.id);
    setSelectedIds(all.slice(0, BATCH_LIMIT));
    if (all.length > BATCH_LIMIT) {
      toast.warning(`Selected the first ${BATCH_LIMIT} of ${all.length} pieces (batch limit).`);
    }
  };
  const exitSelectMode = () => { setSelectMode(false); setSelectedIds([]); };

  // Reflect an edited jewellery number back into the list (and thus the batch
  // dialog) without a full refetch.
  const handleNumberUpdated = (id, newNo) => {
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, jewellery_no: newNo } : it)));
  };

  // ------------------------------------------------------------------
  // Print Image List — fires a backend POST that returns a PDF stream.
  // We pass the *filtered* set of jewellery ids so the PDF mirrors what
  // the user sees on screen, plus the operator's branding / show-details
  // toggles.
  // ------------------------------------------------------------------
  const handlePrint = async () => {
    if (filtered.length === 0) {
      toast.error('No jewellery in the current filter to print');
      return;
    }
    setPrintDialog((p) => ({ ...p, busy: true }));
    try {
      const payload = {
        company_id: effectiveCompanyId,
        jewellery_ids: filtered.map((j) => j.id),
        include_branding: printDialog.includeBranding,
        show_details: printDialog.showDetails,
        filter_summary: buildFilterSummary({ search, goldsmithFilter, goldsmithOptions, dateFrom, dateTo, total: filtered.length }),
      };
      const res = await api.post('/jewellery/image-list/pdf', payload, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const ymd = new Date().toISOString().slice(0, 10);
      a.download = `Jewellery_ImageList_${ymd}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Printed ${filtered.length} jewellery tile(s)`);
      setPrintDialog((p) => ({ ...p, open: false, busy: false }));
    } catch (e) {
      toast.error(extractError(e, 'Failed to generate image list PDF'));
      setPrintDialog((p) => ({ ...p, busy: false }));
    }
  };

  // Aggregated KPIs computed off the *filtered* list so they react
  // live to the search + goldsmith filter.
  const kpis = useMemo(() => {
    const total = filtered.length;
    const totalGross = filtered.reduce((s, j) => s + (j.gross_weight_grams || 0), 0);
    const totalNet = filtered.reduce((s, j) => s + (j.net_weight_grams || 0), 0);
    const totalCost = filtered.reduce((s, j) => s + (j.cost_value || 0), 0);
    return { total, totalGross, totalNet, totalCost };
  }, [filtered]);

  return (
    <div className="space-y-4" data-testid="jewellery-list-page">
      {/* KPI strip — 4 tiles per Wave 3.24. Wave 3.41 — drop Total Value (cost) for users without values.view */}
      <div className={`grid grid-cols-2 ${canViewValues ? 'lg:grid-cols-4' : 'lg:grid-cols-3'} gap-3`}>
        <Card className="p-4 bg-gradient-to-br from-amber-50 to-white border-amber-200">
          <div className="text-[11px] uppercase tracking-wide text-amber-700">Pieces</div>
          <div className="text-2xl font-mono text-[var(--ink-navy)] mt-1" data-testid="jewellery-kpi-count">
            {kpis.total}
          </div>
        </Card>
        <Card className="p-4">
          <div className="text-[11px] uppercase tracking-wide text-slate-500">Total Gross Weight (g)</div>
          <div className="text-2xl font-mono text-[var(--ink-navy)] mt-1" data-testid="jewellery-kpi-gross-weight">
            {formatGrams(kpis.totalGross)}
          </div>
        </Card>
        <Card className="p-4">
          <div className="text-[11px] uppercase tracking-wide text-slate-500">Total Net Weight (g)</div>
          <div className="text-2xl font-mono text-[var(--ink-navy)] mt-1" data-testid="jewellery-kpi-net-weight">
            {formatGrams(kpis.totalNet)}
          </div>
        </Card>
        {canViewValues && (
          <Card
            className="p-4 bg-gradient-to-br from-emerald-50 to-white border-emerald-200"
            title="Manufacturing cost = raw-material consumed value + product labour cost (labour + gold-loss value)."
          >
            <div className="text-[11px] uppercase tracking-wide text-emerald-700">Total Value (at cost)</div>
            <div className="text-2xl font-mono text-emerald-800 mt-1" data-testid="jewellery-kpi-total-cost">
              {formatINR(kpis.totalCost)}
            </div>
          </Card>
        )}
      </div>

      {/* Filter bar — Wave 3.25: includes date range + Print Image List */}
      <Card className="p-3 space-y-3 sticky-filter-bar" data-testid="jewellery-filter-bar">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 flex-1 min-w-[240px]">
            <Search size={14} className="text-slate-400" />
            <Input
              placeholder="Search by jewellery no, title, type, purity, gross weight, goldsmith…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-9"
              data-testid="jewellery-search-input"
            />
          </div>
          <div className="w-[200px]">
            <SearchableSelect
              items={[{ id: 'all', name: 'All Goldsmiths' }, ...goldsmithOptions]}
              value={goldsmithFilter}
              onValueChange={(v) => setGoldsmithFilter(v || 'all')}
              itemToLabel={(g) => g.name}
              searchKeys={['name']}
              placeholder="All Goldsmiths"
              searchPlaceholder="Search goldsmith…"
              testId="jewellery-goldsmith-filter"
            />
          </div>
          <Badge variant="outline" className="font-mono text-[11px]" data-testid="jewellery-result-count">
            {filtered.length} of {items.length}
          </Badge>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <Label className="text-[10px] uppercase tracking-wide text-slate-500" htmlFor="jewellery-date-from">Received from</Label>
            <DatePicker
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="h-9 w-[170px] mt-0.5"
              data-testid="jewellery-date-from"
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wide text-slate-500" htmlFor="jewellery-date-to">Received to</Label>
            <DatePicker
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="h-9 w-[170px] mt-0.5"
              data-testid="jewellery-date-to"
            />
          </div>
          {hasActiveFilter && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearFilters}
              className="h-9 text-slate-500"
              data-testid="jewellery-clear-filters"
            >
              <X size={12} className="mr-1" />
              Clear filters
            </Button>
          )}
          <Button
            variant={selectMode ? 'default' : 'outline'}
            size="sm"
            onClick={() => (selectMode ? exitSelectMode() : setSelectMode(true))}
            className={`h-9 ml-auto ${selectMode ? 'bg-[var(--ink-navy)] hover:bg-[var(--ink-navy-2)] text-white' : ''}`}
            data-testid="jewellery-select-mode-toggle"
            title="Select multiple pieces to batch-print their tags"
          >
            <ListChecks size={14} className="mr-1.5" />
            {selectMode ? 'Done selecting' : 'Select for Batch'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPrintDialog((p) => ({ ...p, open: true }))}
            className={`h-9 ${selectMode ? '' : 'ml-0'}`}
            disabled={filtered.length === 0}
            data-testid="jewellery-print-image-list"
            title="Print the current filtered list as an Image List PDF (4 tiles per row, portrait)"
          >
            <Printer size={14} className="mr-1.5" />
            Print Image List
          </Button>
        </div>
      </Card>

      {/* Batch selection action bar — appears in select mode */}
      {selectMode && (
        <Card
          className="p-3 flex flex-wrap items-center gap-3 border-[var(--jewellery-border)] bg-[var(--jewellery-bg)]/40 sticky top-0 z-10"
          data-testid="jewellery-batch-bar"
        >
          <div className="flex items-center gap-2">
            <Badge
              variant="outline"
              className={`font-mono text-[11px] ${selectedIds.length >= BATCH_LIMIT ? 'border-amber-300 text-amber-700 bg-amber-50' : ''}`}
              data-testid="jewellery-batch-selected-count"
            >
              {selectedIds.length} / {BATCH_LIMIT} selected
            </Badge>
            <span className="text-[11px] text-slate-500">Tick cards below, or use the actions here.</span>
          </div>
          <div className="flex items-center gap-2 ml-auto">
            <Button
              variant="outline"
              size="sm"
              onClick={selectAllFiltered}
              className="h-8"
              disabled={filtered.length === 0}
              data-testid="jewellery-batch-select-all"
            >
              <CheckCheck size={13} className="mr-1.5" /> Select all (filtered)
            </Button>
            {selectedIds.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedIds([])}
                className="h-8 text-slate-500"
                data-testid="jewellery-batch-clear"
              >
                <X size={13} className="mr-1" /> Clear
              </Button>
            )}
            <Button
              size="sm"
              onClick={() => setBatchOpen(true)}
              className="h-8 bg-[var(--ink-navy)] hover:bg-[var(--ink-navy-2)] text-white"
              disabled={selectedIds.length === 0}
              data-testid="jewellery-batch-print-open"
            >
              <Printer size={13} className="mr-1.5" /> Batch Print Tags
            </Button>
          </div>
        </Card>
      )}

      {loading ? (
        <div className="text-slate-500 py-10" data-testid="jewellery-loading">Loading…</div>
      ) : filtered.length === 0 ? (
        <Card className="p-10 text-center" data-testid="jewellery-empty-state">
          <Diamond size={32} className="text-amber-500 mx-auto mb-3" />
          <div className="text-[var(--ink-navy)] font-medium">
            {items.length === 0 ? 'No jewellery received yet' : 'No matches'}
          </div>
          <div className="text-xs text-slate-500 mt-1">
            {items.length === 0
              ? 'When a goldsmith hands back the finished piece, use the Receive Jewellery wizard on the order page.'
              : 'Try clearing the search or goldsmith filter.'}
          </div>
        </Card>
      ) : (
        <>
          <div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3"
            data-testid="jewellery-grid"
          >
            {pageItems.map((j) => (
              <JewelleryCard
                key={j.id}
                j={j}
                selectMode={selectMode}
                selected={selectedIds.includes(j.id)}
                onToggleSelect={toggleSelect}
                onNumberUpdated={handleNumberUpdated}
              />
            ))}
          </div>

          {/* Pagination — 30 cards per page */}
          <div className="mt-4 flex flex-wrap items-center justify-center gap-3 sm:justify-between" data-testid="jewellery-pagination">
            <div className="text-sm text-slate-500" data-testid="jewellery-pagination-summary">
              Showing <span className="font-medium text-[var(--ink-navy)]">{(page - 1) * PAGE_SIZE + 1}</span>–
              <span className="font-medium text-[var(--ink-navy)]">{(page - 1) * PAGE_SIZE + pageItems.length}</span>
              {' '}of <span className="font-medium text-[var(--ink-navy)]">{filtered.length}</span>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                className="h-9"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                data-testid="jewellery-pagination-prev"
              >
                <ChevronLeft size={15} className="mr-1" /> Prev
              </Button>
              <span className="px-1 text-sm text-slate-500" data-testid="jewellery-pagination-indicator">
                Page <span className="font-medium text-[var(--ink-navy)]">{page}</span> of <span className="font-medium text-[var(--ink-navy)]">{totalPages}</span>
              </span>
              <Button
                variant="outline"
                size="sm"
                className="h-9"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                data-testid="jewellery-pagination-next"
              >
                Next <ChevronRight size={15} className="ml-1" />
              </Button>
            </div>
          </div>
        </>
      )}

      {/* Print Image List dialog — Wave 3.25 */}
      <Dialog open={printDialog.open} onOpenChange={(o) => setPrintDialog((p) => ({ ...p, open: o }))}>
        <DialogContent className="max-w-md" data-testid="jewellery-print-dialog">
          <DialogHeader>
            <DialogTitle>Print Image List</DialogTitle>
            <DialogDescription>
              Generates a portrait A4 PDF with 4 jewellery tiles per row, mirroring
              the {filtered.length} item{filtered.length === 1 ? '' : 's'} currently visible
              under the active filter.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="flex items-start justify-between gap-3 rounded-md border border-slate-200 p-3">
              <div>
                <div className="text-sm font-medium text-[var(--ink-navy)]">Include Branding</div>
                <div className="text-[11px] text-slate-500 mt-0.5">Adds the company logo + name to the page header.</div>
              </div>
              <Switch
                checked={printDialog.includeBranding}
                onCheckedChange={(v) => setPrintDialog((p) => ({ ...p, includeBranding: v }))}
                data-testid="jewellery-print-branding-toggle"
              />
            </div>
            <div className="flex items-start justify-between gap-3 rounded-md border border-slate-200 p-3">
              <div>
                <div className="text-sm font-medium text-[var(--ink-navy)]">Show Jewellery Details</div>
                <div className="text-[11px] text-slate-500 mt-0.5">Includes Net weight, Diamond / Colour Stone / Pearl carats + pieces, and Value at cost beneath each image.</div>
              </div>
              <Switch
                checked={printDialog.showDetails}
                onCheckedChange={(v) => setPrintDialog((p) => ({ ...p, showDetails: v }))}
                data-testid="jewellery-print-details-toggle"
              />
            </div>
            <div className="text-[11px] text-slate-500 leading-snug">
              The image is sized so the full jewellery piece is visible. Tiles never
              split across pages — page-break is calculated row-wise.
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setPrintDialog((p) => ({ ...p, open: false }))}
              disabled={printDialog.busy}
            >
              Cancel
            </Button>
            <Button
              onClick={handlePrint}
              disabled={printDialog.busy || filtered.length === 0}
              className="luxury-button"
              data-testid="jewellery-print-confirm"
            >
              {printDialog.busy ? 'Generating…' : `Print ${filtered.length} tile${filtered.length === 1 ? '' : 's'}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Batch Tag Printing dialog */}
      <JewelleryBatchPrintDialog
        open={batchOpen}
        onOpenChange={setBatchOpen}
        companyId={effectiveCompanyId}
        items={filtered}
        initialSelectedIds={selectedIds}
      />
    </div>
  );
}

// Wave 3.25 hot-fix — Compact one-liner shown at the top of the printed
// PDF so the operator knows what filter was active when the list was
// generated.
function buildFilterSummary({ search, goldsmithFilter, goldsmithOptions, dateFrom, dateTo, total }) {
  const parts = [];
  if (search) parts.push(`Search: "${search}"`);
  if (goldsmithFilter !== 'all') {
    const g = goldsmithOptions.find((x) => x.id === goldsmithFilter);
    if (g) parts.push(`Goldsmith: ${g.name}`);
  }
  if (dateFrom || dateTo) {
    parts.push(`Received: ${dateFrom || '∞'} → ${dateTo || '∞'}`);
  }
  parts.push(`${total} piece${total === 1 ? '' : 's'}`);
  return parts.join(' · ');
}

// ============================================================================
// Wave 3.25 — Tile redesign.
//
// Always visible (compact "at a glance" header):
//   • Jewellery No + arrow
//   • Title
//   • Type of Jewellery pill (NEW)
//   • Date of Receipt + Gold Purity (NEW)
//   • Value (at cost)
//   • Goldsmith + Source Order pill
//
// Hidden behind a per-card "Show Details" toggle (default = collapsed):
//   • Gross + Net weight
//   • Diamond / Colour Stone / Pearl carat breakdown
//
// Rationale: users wanted a cleaner overview tile and to drill into the
// detailed numbers only when needed. The toggle is local to each card so
// the user can keep the details open on the piece they're investigating
// without flipping the entire grid.
// ============================================================================
function JewelleryCard({ j, selectMode = false, selected = false, onToggleSelect, onNumberUpdated }) {
  // Wave 3.41 — Restricted users hide the Value (at cost) stripe.
  const { canViewValues, isSuperAdmin, isCompanyAdmin } = useAuth();
  const canEditNo = isSuperAdmin || isCompanyAdmin;
  const [showDetails, setShowDetails] = useState(false);
  const [tagOpen, setTagOpen] = useState(false);
  const fileRef = useRef(null);
  const [img, setImg] = useState(j.image);
  const [uploadingImg, setUploadingImg] = useState(false);
  // Inline jewellery-number edit (Company Admin + Super Admin).
  const [editingNo, setEditingNo] = useState(false);
  const [noDraft, setNoDraft] = useState(j.jewellery_no || '');
  const [savingNo, setSavingNo] = useState(false);

  const saveNo = async () => {
    const v = (noDraft || '').trim();
    if (!v) { toast.error('Jewellery number cannot be empty'); return; }
    if (v === (j.jewellery_no || '')) { setEditingNo(false); return; }
    setSavingNo(true);
    try {
      const res = await api.patch(`/jewellery/${j.id}/number`, { jewellery_no: v });
      onNumberUpdated?.(j.id, res?.data?.jewellery_no || v);
      toast.success('Jewellery number updated');
      setEditingNo(false);
    } catch (e) {
      toast.error(extractError(e, 'Failed to update jewellery number'));
    } finally {
      setSavingNo(false);
    }
  };

  const handleCardImage = async (file) => {
    if (!file) return;
    setUploadingImg(true);
    try {
      const dataUrl = await fileToResizedDataUrl(file);
      const res = await api.post(`/jewellery/${j.id}/image`, { image_data_url: dataUrl });
      setImg(res?.data?.image || dataUrl);
      toast.success('Image updated — the tag will show the new photo');
    } catch (e) {
      toast.error(extractError(e, 'Failed to update image'));
    } finally {
      setUploadingImg(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };
  const comps = j.components || {};
  const dCt = Number(comps.diamond_carats || 0);
  const csCt = Number(comps.color_stone_carats || 0);
  const pCt = Number(comps.pearl_carats || 0);
  const hasStones = dCt > 0 || csCt > 0 || pCt > 0;
  const purityName = j.gold_purity_name || (j.gold_purity_percent ? `${Number(j.gold_purity_percent).toFixed(2)}%` : '');
  const receivedAt = j.received_at ? formatDate(j.received_at) : '—';

  return (
    <div
      className={`block group rounded-lg ${selected ? 'ring-2 ring-[var(--jewellery-icon)] ring-offset-1' : ''}`}
      data-testid={`jewellery-card-${j.id}`}
    >
      <Card className={`overflow-hidden hover:shadow-md transition-all h-full flex flex-col ${j.repair_status === 'in_repair' ? 'border-amber-300 bg-slate-100/70' : 'hover:border-emerald-200 border-slate-200'}`}>
        {/* Repair status banner — vivid (not greyed) so it reads clearly. */}
        {j.repair_status === 'in_repair' ? (
          <div className="px-3 py-1 bg-amber-100 border-b border-amber-200 flex items-center gap-1.5 text-[10px] font-semibold text-amber-800" data-testid={`jewellery-card-repair-banner-${j.id}`}>
            <Hammer size={11} className="shrink-0" /> Sent for repairing{j.repair_lock_order_no ? ` · ${j.repair_lock_order_no}` : ''}
          </div>
        ) : j.last_repaired_at ? (
          <div className="px-3 py-1 bg-emerald-50 border-b border-emerald-100 flex items-center gap-1.5 text-[10px] font-semibold text-emerald-700" data-testid={`jewellery-card-last-repaired-${j.id}`}>
            <Hammer size={11} className="shrink-0" /> Last Repaired On {formatDate(j.last_repaired_at)}
          </div>
        ) : null}
        {/* ── Hero row: compact square image + stacked metadata.
            Wave 3.30 — replaces the wide 4:3 image banner that left a
            big empty letterbox area beside the photo. */}
        <Link
          to={`/jewellery/${j.id}`}
          onClick={(e) => {
            if (selectMode) {
              e.preventDefault();
              e.stopPropagation();
              onToggleSelect?.(j.id);
            }
          }}
          className="flex gap-3 p-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 rounded-t-lg group/hero"
          data-testid={`jewellery-card-link-${j.id}`}
        >
          <div className="shrink-0 w-[88px] h-[88px] rounded-md border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden relative">
            {/* Batch-select checkbox overlay (select mode only) */}
            {selectMode && (
              <span
                className="absolute top-0.5 left-0.5 z-10 inline-flex items-center justify-center w-6 h-6 rounded-md bg-white/95 border border-slate-200 shadow-sm"
                data-testid={`jewellery-card-select-${j.id}`}
              >
                <Checkbox checked={selected} className="pointer-events-none" tabIndex={-1} />
              </span>
            )}
            {img ? (
              <img src={img} alt={j.jewellery_no} className={`w-full h-full object-contain p-1 ${j.repair_status === 'in_repair' ? 'grayscale opacity-60' : ''}`} />
            ) : j.fallback_image ? (
              <>
                <img
                  src={j.fallback_image}
                  alt={j.jewellery_no}
                  className="w-full h-full object-contain p-1 opacity-85"
                />
                <Badge
                  variant="outline"
                  className="absolute top-0.5 right-0.5 bg-white/95 text-[8px] font-normal px-1 py-0 leading-tight"
                >
                  Order ref
                </Badge>
              </>
            ) : (
              <ImageIcon size={26} className="text-slate-300" />
            )}
            {/* Replace-image control — always visible, bottom-right. It lives
                inside the card Link, so stop navigation when clicked. */}
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (!uploadingImg) fileRef.current?.click(); }}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); fileRef.current?.click(); } }}
              title={img ? 'Replace image' : 'Add image'}
              aria-label={img ? 'Replace image' : 'Add image'}
              data-testid={`jewellery-card-edit-image-${j.id}`}
              className="absolute bottom-0.5 right-0.5 inline-flex items-center justify-center w-6 h-6 rounded-md bg-white/95 border border-slate-200 text-[var(--jewellery-icon)] shadow-sm hover:bg-[var(--jewellery-bg)] cursor-pointer transition-colors"
            >
              {uploadingImg ? <Loader2 size={12} className="animate-spin" /> : <Pencil size={12} />}
            </span>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              className="hidden"
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => handleCardImage(e.target.files?.[0])}
            />
          </div>
          <div className="flex-1 min-w-0 flex flex-col gap-1">
            <div className="flex items-center justify-between gap-2">
              {editingNo ? (
                <div
                  className="flex items-center gap-1 min-w-0"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
                >
                  <input
                    value={noDraft}
                    onChange={(e) => setNoDraft(e.target.value)}
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
                    onKeyDown={(e) => {
                      e.stopPropagation();
                      if (e.key === 'Enter') { e.preventDefault(); saveNo(); }
                      if (e.key === 'Escape') { e.preventDefault(); setEditingNo(false); }
                    }}
                    autoFocus
                    disabled={savingNo}
                    className="font-mono text-sm text-emerald-700 border border-[var(--jewellery-border)] rounded px-1.5 py-0.5 w-[140px] focus:outline-none focus:ring-2 focus:ring-[var(--jewellery-icon)]"
                    data-testid={`jewellery-card-no-input-${j.id}`}
                  />
                  <button
                    type="button"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); saveNo(); }}
                    disabled={savingNo}
                    title="Save"
                    aria-label="Save jewellery number"
                    data-testid={`jewellery-card-no-save-${j.id}`}
                    className="inline-flex items-center justify-center w-6 h-6 rounded text-emerald-700 hover:bg-emerald-50 transition-colors disabled:opacity-50"
                  >
                    {savingNo ? <Loader2 size={13} className="animate-spin" /> : <Check size={14} />}
                  </button>
                  <button
                    type="button"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); setEditingNo(false); }}
                    disabled={savingNo}
                    title="Cancel"
                    aria-label="Cancel editing"
                    data-testid={`jewellery-card-no-cancel-${j.id}`}
                    className="inline-flex items-center justify-center w-6 h-6 rounded text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors disabled:opacity-50"
                  >
                    <X size={14} />
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-1.5 min-w-0">
                  <div
                    className="font-mono text-sm text-emerald-700 leading-none truncate"
                    data-testid={`jewellery-card-no-${j.id}`}
                  >
                    {j.jewellery_no}
                  </div>
                  {canEditNo && (
                    <button
                      type="button"
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); setNoDraft(j.jewellery_no || ''); setEditingNo(true); }}
                      title="Edit jewellery number"
                      aria-label="Edit jewellery number"
                      data-testid={`jewellery-card-no-edit-${j.id}`}
                      className="inline-flex items-center justify-center w-5 h-5 rounded text-slate-400 hover:text-[var(--jewellery-icon)] hover:bg-[var(--jewellery-bg)] transition-colors shrink-0"
                    >
                      <Pencil size={11} />
                    </button>
                  )}
                </div>
              )}
              <ArrowRight
                size={14}
                className="text-slate-400 group-hover:text-emerald-700 transition-colors shrink-0"
              />
            </div>
            <div
              className="text-sm font-medium text-[var(--ink-navy)] leading-snug line-clamp-2"
              title={j.title}
              data-testid={`jewellery-card-title-${j.id}`}
            >
              {j.title || '(untitled)'}
            </div>
            <div className="flex items-center gap-1.5 text-[11px]" data-testid={`jewellery-card-type-${j.id}`}>
              <Tag size={10} className="text-[var(--jewellery-icon)] shrink-0" />
              <span className="text-slate-700 truncate" title={j.jewellery_type || ''}>
                {j.jewellery_type || <span className="text-slate-400 italic">No type set</span>}
              </span>
            </div>
            <div className="flex items-center gap-2 text-[11px] mt-auto">
              <span
                className="inline-flex items-center gap-1 text-slate-500 font-mono"
                title={`Received ${receivedAt}`}
                data-testid={`jewellery-card-received-${j.id}`}
              >
                <CalendarDays size={10} /> {receivedAt}
              </span>
              <span className="text-slate-300">·</span>
              <span
                className="inline-flex items-center gap-1 text-amber-800 font-mono"
                title={`Gold purity ${purityName || '—'}`}
                data-testid={`jewellery-card-purity-${j.id}`}
              >
                <Coins size={10} /> {purityName || '—'}
              </span>
            </div>
          </div>
        </Link>

        <div className="px-3 pb-3 space-y-2 flex-1 flex flex-col border-t border-slate-100 pt-2">
          {/* Show Details toggle — gates the weight/stone breakdown.
              Collapsed by default per Wave 3.25 spec.
              Wave 4.14 — Paired with a Print Tag action. */}
          <div className="flex items-center gap-1.5">
            <Button
              variant="ghost"
              size="sm"
              type="button"
              onClick={(e) => { e.preventDefault(); setShowDetails((v) => !v); }}
              className="h-7 -mx-1 flex-1 justify-between text-[11px] text-slate-600 hover:bg-slate-50"
              data-testid={`jewellery-card-toggle-${j.id}`}
              aria-expanded={showDetails}
            >
              <span className="flex items-center gap-1.5">
                {showDetails ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                {showDetails ? 'Hide Details' : 'Show Details'}
              </span>
              <span className="text-[10px] text-slate-400">
                {formatGrams(j.net_weight_grams || 0)} g net
              </span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              type="button"
              onClick={(e) => { e.preventDefault(); setTagOpen(true); }}
              className="h-7 px-2 text-[11px] border-[var(--jewellery-border)] text-[var(--jewellery-icon)] hover:bg-[var(--jewellery-bg)]"
              data-testid={`jewellery-tag-button-${j.id}`}
              title={`Print tag for ${j.jewellery_no}`}
            >
              <Printer size={12} className="mr-1" /> Tag
            </Button>
          </div>

          {showDetails && (
            <div className="space-y-2" data-testid={`jewellery-card-details-${j.id}`}>
              {/* Gross + Net weights */}
              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-md bg-slate-50 border border-slate-100 px-2 py-1.5">
                  <div className="text-[9px] uppercase tracking-wide text-slate-500">Gross</div>
                  <div className="text-[12px] font-mono text-[var(--ink-navy)]">
                    {formatGrams(j.gross_weight_grams || 0)} g
                  </div>
                </div>
                <div className="rounded-md bg-slate-50 border border-slate-100 px-2 py-1.5">
                  <div className="text-[9px] uppercase tracking-wide text-slate-500">Net</div>
                  <div className="text-[12px] font-mono text-[var(--ink-navy)]">
                    {formatGrams(j.net_weight_grams || 0)} g
                  </div>
                </div>
              </div>

              {/* Stone weight breakdown — hide rows that are zero */}
              {hasStones ? (
                <div className="space-y-0.5 text-[11px]" data-testid={`jewellery-card-stones-${j.id}`}>
                  {dCt > 0 && (
                    <div className="flex items-center justify-between text-sky-700">
                      <span className="flex items-center gap-1">
                        <Gem size={11} /> Diamonds
                      </span>
                      <span className="font-mono">
                        {formatCarats(dCt)} ct{comps.diamond_pieces ? ` · ${comps.diamond_pieces} pcs` : ''}
                      </span>
                    </div>
                  )}
                  {csCt > 0 && (
                    <div className="flex items-center justify-between text-rose-700">
                      <span className="flex items-center gap-1">
                        <Sparkles size={11} /> Colour Stones
                      </span>
                      <span className="font-mono">
                        {formatCarats(csCt)} ct{comps.color_stone_pieces ? ` · ${comps.color_stone_pieces} pcs` : ''}
                      </span>
                    </div>
                  )}
                  {pCt > 0 && (
                    <div className="flex items-center justify-between text-slate-600">
                      <span className="flex items-center gap-1">
                        <Circle size={11} /> Pearls
                      </span>
                      <span className="font-mono">
                        {formatCarats(pCt)} ct{comps.pearl_pieces ? ` · ${comps.pearl_pieces} pcs` : ''}
                      </span>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-[10px] text-slate-400 italic">No stones in this piece.</div>
              )}
            </div>
          )}

          {/* Value at cost — premium emerald stripe. Wave 3.41 — hidden when !canViewValues */}
          {canViewValues && (
            <div className="pt-1 mt-auto border-t border-slate-100 flex items-baseline justify-between">
              <span className="text-[10px] uppercase tracking-wide text-emerald-700">Value (at cost)</span>
              <span className="font-mono text-emerald-800 text-sm" data-testid={`jewellery-card-cost-${j.id}`}>
                {formatINR(j.cost_value || 0)}
              </span>
            </div>
          )}

          {/* Goldsmith pinned to bottom */}
          <div
            className="flex items-center gap-1 text-[11px] text-slate-600 pt-1 border-t border-slate-100"
            title={j.goldsmith_name}
            data-testid={`jewellery-card-goldsmith-${j.id}`}
          >
            <Hammer size={11} className="shrink-0 text-slate-400" />
            <span className="truncate">{j.goldsmith_name || '—'}</span>
            {j.source_order_no && (
              <span className="ml-auto inline-flex items-center gap-1 text-slate-400 font-mono text-[10px]">
                <ScrollText size={10} /> {j.source_order_no}
              </span>
            )}
          </div>
        </div>
      </Card>
      <JewelleryTagDialog
        open={tagOpen}
        onOpenChange={setTagOpen}
        jewelleryId={j.id}
        jewelleryNo={j.jewellery_no}
      />
    </div>
  );
}
